import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { useSnackbar } from 'react-simple-snackbar'
import {
  fetchRhCampaign,
  sendRhCampaignEmails,
  sendRhSingleInvoiceEmail,
  previewVirementExport,
  downloadVirementExport,
} from '../../api/index'
import { downloadInvoicePdf, printInvoicePdf } from '../../utils/invoicePdf'
import * as api from '../../api/index'
import { isAdmin } from '../../utils/auth'
import { useRhGuard } from '../../hooks/useRoleGuard'
import Spinner from '../Spinner/Spinner'
import ConfirmModal from '../ConfirmModal/ConfirmModal'
import BackButton from '../BackButton/BackButton'
import { formatTND } from '../../utils/formatTND'
import styles from './CampagneDetail.module.css'

const MONTHS = [
  '', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
]

const STATUS_OPTIONS = [
  { value: 'all', label: 'Tous les statuts' },
  { value: 'sent', label: 'Envoyées' },
  { value: 'pending', label: 'En attente' },
  { value: 'failed', label: 'Échecs' },
]

const statusLabel = {
  sent: { text: 'Envoyée', cls: 'sent' },
  failed: { text: 'Échec', cls: 'failed' },
  pending: { text: 'En attente', cls: 'pending' },
}

const triggerBlobDownload = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  link.click()
  window.URL.revokeObjectURL(url)
}

const formatPeriod = (period) => {
  if (!period) return '—'
  const [year, month] = period.split('-')
  return `${MONTHS[Number(month)]} ${year}`
}

const PAGE_SIZE = 10

const CampagneDetail = () => {
  const { id } = useParams()
  const history = useHistory()
  const [openSnackbar] = useSnackbar()
  useRhGuard()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState([])
  const [sending, setSending] = useState(false)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [page, setPage] = useState(1)
  const [deleting, setDeleting] = useState(false)
  const [confirmModal, setConfirmModal] = useState(null)
  const [virementPreview, setVirementPreview] = useState(null)
  const [virementLoading, setVirementLoading] = useState(false)
  const [virementError, setVirementError] = useState('')
  const [downloadingVirement, setDownloadingVirement] = useState(false)
  const admin = isAdmin()

  const fetchCampaign = useCallback(async () => {
    try {
      const { data: result } = await fetchRhCampaign(id)
      setData(result)
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    fetchCampaign()
  }, [fetchCampaign])

  useEffect(() => {
    if (!data || sending) return
    const params = new URLSearchParams(window.location.search)
    if (params.get('sendAll') === '1') {
      window.history.replaceState({}, '', `/campagnes/${id}`)
      executeSendEmails(null)
    }
    // eslint-disable-next-line
  }, [data])

  useEffect(() => {
    setPage(1)
  }, [search, statusFilter])

  const { campaign, invoices = [] } = data || {}

  const [periodYear, periodMonth] = useMemo(() => {
    if (!campaign?.period) return [null, null]
    const [year, month] = campaign.period.split('-').map(Number)
    return [year, month]
  }, [campaign?.period])

  const loadVirementPreview = useCallback(async () => {
    if (!admin || !periodMonth || !periodYear || campaign?.status !== 'completed') {
      setVirementPreview(null)
      setVirementError('')
      return
    }

    setVirementLoading(true)
    setVirementError('')
    try {
      const { data: preview } = await previewVirementExport({ month: periodMonth, year: periodYear })
      setVirementPreview(preview)
    } catch (error) {
      setVirementPreview(null)
      setVirementError(error?.response?.data?.message || 'Impossible de charger l\'aperçu virement')
    } finally {
      setVirementLoading(false)
    }
  }, [admin, periodMonth, periodYear, campaign?.status])

  useEffect(() => {
    loadVirementPreview()
  }, [loadVirementPreview])

  const stats = useMemo(() => {
    const sent = invoices.filter((i) => i.emailStatus === 'sent').length
    const failed = invoices.filter((i) => i.emailStatus === 'failed').length
    const pending = invoices.filter((i) => !i.emailStatus || i.emailStatus === 'pending').length
    const totalHonoraires = invoices.reduce((acc, inv) => acc + (inv.honoraires || inv.subTotal || 0), 0)
    const totalNet = invoices.reduce((acc, inv) => acc + (inv.netAPayer || inv.total || 0), 0)
    const emailTotal = sent + pending + failed
    const emailProgress = emailTotal > 0 ? Math.round((sent / emailTotal) * 100) : 0

    return { sent, failed, pending, totalHonoraires, totalNet, emailProgress }
  }, [invoices])

  const filteredInvoices = useMemo(() => {
    const q = search.trim().toLowerCase()
    return invoices.filter((inv) => {
      const status = inv.emailStatus || 'pending'
      if (statusFilter !== 'all' && status !== statusFilter) return false
      if (!q) return true
      const haystack = [
        inv.invoiceNumber,
        inv.coach?.name,
        inv.coach?.email,
        inv.coach?.mf,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return haystack.includes(q)
    })
  }, [invoices, search, statusFilter])

  const totalPages = Math.max(1, Math.ceil(filteredInvoices.length / PAGE_SIZE))
  const safePage = Math.min(page, totalPages)
  const paginatedInvoices = filteredInvoices.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE
  )

  const toggleSelect = (invoiceId) => {
    setSelected((prev) =>
      prev.includes(invoiceId) ? prev.filter((x) => x !== invoiceId) : [...prev, invoiceId]
    )
  }

  const toggleSelectAll = () => {
    const ids = filteredInvoices.map((inv) => inv._id)
    const allSelected = ids.length > 0 && ids.every((id) => selected.includes(id))
    if (allSelected) {
      setSelected((prev) => prev.filter((id) => !ids.includes(id)))
    } else {
      setSelected((prev) => [...new Set([...prev, ...ids])])
    }
  }

  const downloadPdf = async (invoiceId, invoiceNumber) => {
    try {
      await downloadInvoicePdf(id, invoiceId, invoiceNumber)
    } catch {
      openSnackbar('Erreur téléchargement PDF')
    }
  }

  const printPdf = async (invoiceId) => {
    try {
      await printInvoicePdf(id, invoiceId)
    } catch {
      openSnackbar('Erreur impression PDF')
    }
  }

  const executeSendEmails = async (invoiceIds = null) => {
    setSending(true)
    try {
      const { data: result } = await sendRhCampaignEmails(id, { invoiceIds: invoiceIds || undefined })
      openSnackbar(result.message)
      fetchCampaign()
      setSelected([])
    } catch (error) {
      openSnackbar(error?.response?.data?.message || 'Erreur envoi emails')
    } finally {
      setSending(false)
    }
  }

  const requestSendEmails = (invoiceIds = null) => {
    const count = invoiceIds ? invoiceIds.length : invoices.length
    if (!count) {
      openSnackbar('Aucune facture à envoyer')
      return
    }
    setConfirmModal({
      type: 'sendBulk',
      invoiceIds,
      variant: 'info',
      title: 'Envoyer les emails',
      message: invoiceIds
        ? `Envoyer ${count} facture(s) sélectionnée(s) par email ?`
        : `Envoyer toutes les factures (${count}) par email ?`,
      detail: 'Chaque coach recevra sa facture en PDF par email.',
      confirmLabel: 'Envoyer',
    })
  }

  const executeSendOne = async (invoiceId) => {
    setSending(true)
    try {
      const { data: result } = await sendRhSingleInvoiceEmail(id, invoiceId)
      openSnackbar(result.message)
      fetchCampaign()
    } catch (error) {
      openSnackbar(error?.response?.data?.message || 'Erreur envoi email')
    } finally {
      setSending(false)
    }
  }

  const requestSendOne = (invoiceId, alreadySent = false, coachName = '') => {
    setConfirmModal({
      type: 'sendOne',
      invoiceId,
      variant: 'info',
      title: alreadySent ? 'Renvoyer la facture' : 'Envoyer la facture',
      message: alreadySent
        ? 'Renvoyer cette facture par email au coach ?'
        : 'Envoyer cette facture par email au coach ?',
      detail: coachName ? `Destinataire : ${coachName}` : undefined,
      confirmLabel: alreadySent ? 'Renvoyer' : 'Envoyer',
    })
  }

  const executeDeleteCampaign = async () => {
    setDeleting(true)
    try {
      await api.deleteCampaign(id)
      openSnackbar('Campagne supprimée')
      history.push('/dashboard')
    } catch (error) {
      openSnackbar(error?.response?.data?.message || 'Erreur lors de la suppression')
    } finally {
      setDeleting(false)
    }
  }

  const requestDeleteCampaign = () => {
    setConfirmModal({
      type: 'deleteCampaign',
      variant: 'warning',
      title: 'Supprimer la campagne',
      message: 'Supprimer cette campagne ?',
      detail: 'Toutes les factures associées seront définitivement supprimées.',
      confirmLabel: 'Supprimer',
    })
  }

  const handleConfirmModal = () => {
    if (!confirmModal) return
    const { type, invoiceIds, invoiceId } = confirmModal
    setConfirmModal(null)
    if (type === 'sendBulk') executeSendEmails(invoiceIds ?? null)
    else if (type === 'sendOne') executeSendOne(invoiceId)
    else if (type === 'deleteCampaign') executeDeleteCampaign()
  }

  const handleDownloadVirement = async () => {
    if (!periodMonth || !periodYear) return
    setDownloadingVirement(true)
    try {
      const { data: blob } = await downloadVirementExport({ month: periodMonth, year: periodYear })
      const slug = `${periodYear}_${String(periodMonth).padStart(2, '0')}`
      triggerBlobDownload(blob, `virement-biat-${slug}.xls`)
      openSnackbar('Fichier virement MyBIAT téléchargé')
    } catch (error) {
      openSnackbar(error?.response?.data?.message || 'Erreur téléchargement virement')
    } finally {
      setDownloadingVirement(false)
    }
  }

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spinner />
      </div>
    )
  }

  if (!data) {
    return (
      <div className={styles.page}>
        <p className={styles.empty}>Campagne introuvable</p>
      </div>
    )
  }

  const filteredIds = filteredInvoices.map((inv) => inv._id)
  const allFilteredSelected =
    filteredIds.length > 0 && filteredIds.every((id) => selected.includes(id))

  return (
    <div className={styles.page}>
      <BackButton onClick={() => history.push('/dashboard')}>
        Retour au tableau de bord
      </BackButton>

      <div className={styles.topBar}>
        <div>
          <h1>Campagne {formatPeriod(campaign.period)}</h1>
          <p>{campaign.fileName} · {campaign.successCount} factures générées</p>
        </div>
        <div className={styles.topActions}>
          {admin && (
            <button
              type="button"
              className={styles.btnDanger}
              disabled={deleting || sending}
              onClick={requestDeleteCampaign}
            >
              {deleting ? 'Suppression...' : 'Supprimer la campagne'}
            </button>
          )}
          <button
            className={styles.btnSecondary}
            disabled={sending || selected.length === 0}
            onClick={() => requestSendEmails(selected)}
          >
            Envoyer sélection ({selected.length})
          </button>
          <button
            className={styles.ctaBtn}
            disabled={sending}
            onClick={() => requestSendEmails(null)}
          >
            {sending ? 'Envoi en cours...' : 'Envoyer tout'}
          </button>
        </div>
      </div>

      <div className={styles.periodBanner}>
        <div>
          <span className={styles.periodLabel}>Période de facturation</span>
          <strong>{formatPeriod(campaign.period)}</strong>
        </div>
        <div className={styles.periodStatus}>
          <span className={campaign.status === 'completed' ? styles.badgeOk : styles.badgeWarn}>
            {campaign.status === 'completed' ? 'Campagne terminée' : campaign.status}
          </span>
          <span>{stats.emailProgress}% emails envoyés</span>
        </div>
      </div>

      <div className={styles.kpiGrid}>
        <div className={`${styles.kpiCard} ${styles.kpiPrimary}`}>
          <span className={styles.kpiLabel}>Coaches facturés</span>
          <span className={styles.kpiValue}>{invoices.length}</span>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>Total honoraires</span>
          <span className={styles.kpiValue}>{formatTND(stats.totalHonoraires)} TND</span>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>Net à payer</span>
          <span className={styles.kpiValue}>{formatTND(stats.totalNet)} TND</span>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>Emails envoyés</span>
          <span className={styles.kpiValue}>{stats.sent} / {invoices.length}</span>
        </div>
      </div>

      <div className={styles.twoCol}>
        <div className={styles.panel}>
          <h2>Statut des envois</h2>
          <div className={styles.emailStats}>
            <div className={styles.emailRow}>
              <span className={styles.dotSent} /> Envoyées
              <strong>{stats.sent}</strong>
            </div>
            <div className={styles.emailRow}>
              <span className={styles.dotPending} /> En attente
              <strong>{stats.pending}</strong>
            </div>
            <div className={styles.emailRow}>
              <span className={styles.dotFailed} /> Échecs
              <strong>{stats.failed}</strong>
            </div>
          </div>
          <div className={styles.progressWrap}>
            <div className={styles.progressBar} style={{ width: `${stats.emailProgress}%` }} />
          </div>
          <p className={styles.progressText}>{stats.emailProgress}% des factures envoyées par email</p>
        </div>

        <div className={styles.panel}>
          <h2>Résumé campagne</h2>
          <ul className={styles.summaryList}>
            <li>
              <span>Période</span>
              <strong>{formatPeriod(campaign.period)}</strong>
            </li>
            <li>
              <span>Fichier source</span>
              <strong>{campaign.fileName}</strong>
            </li>
            <li>
              <span>Factures générées</span>
              <strong>{campaign.successCount}</strong>
            </li>
            <li>
              <span>Client facturé</span>
              <strong>Société Sport et Loisirs</strong>
            </li>
          </ul>
        </div>
      </div>

      <div className={styles.panel}>
        <div className={styles.panelHeader}>
          <h2>Liste des factures</h2>
          <span className={styles.pageInfo}>
            {filteredInvoices.length} résultat(s)
            {(search || statusFilter !== 'all') && ` sur ${invoices.length}`}
          </span>
        </div>

        <div className={styles.toolbar}>
          <input
            type="search"
            className={styles.searchInput}
            placeholder="Rechercher par coach, email, N° facture..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Rechercher une facture"
          />
          <select
            className={styles.filterSelect}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            aria-label="Filtrer par statut d'envoi"
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          {(search || statusFilter !== 'all') && (
            <button
              className={styles.clearBtn}
              onClick={() => { setSearch(''); setStatusFilter('all') }}
            >
              Réinitialiser
            </button>
          )}
        </div>

        {filteredInvoices.length === 0 ? (
          <p className={styles.empty}>Aucune facture ne correspond à votre recherche.</p>
        ) : (
          <>
            <div className={`${styles.tableWrap} table-scroll-wrap`}>
              <table className={`${styles.table} responsive-table`}>
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        checked={allFilteredSelected}
                        onChange={toggleSelectAll}
                        aria-label="Tout sélectionner (filtre actif)"
                      />
                    </th>
                    <th>N° Facture</th>
                    <th>Coach</th>
                    <th>Adresse email</th>
                    <th>Honoraires</th>
                    <th>Net à payer</th>
                    <th>Statut envoi</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedInvoices.map((inv) => {
                    const st = statusLabel[inv.emailStatus || 'pending']
                    return (
                      <tr key={inv._id}>
                        <td data-label="Sélection">
                          <input
                            type="checkbox"
                            checked={selected.includes(inv._id)}
                            onChange={() => toggleSelect(inv._id)}
                            aria-label={`Sélectionner ${inv.coach?.name}`}
                          />
                        </td>
                        <td data-label="N° Facture">{inv.invoiceNumber}</td>
                        <td data-label="Coach">{inv.coach?.name}</td>
                        <td data-label="Adresse email">{inv.coach?.email}</td>
                        <td data-label="Honoraires">{formatTND(inv.honoraires || inv.subTotal)} TND</td>
                        <td data-label="Net à payer">{formatTND(inv.netAPayer || inv.total)} TND</td>
                        <td data-label="Statut envoi">
                          <span className={`${styles.badge} ${styles[st.cls]}`}>{st.text}</span>
                        </td>
                        <td className={styles.actionCell} data-label="Actions">
                          <button
                            type="button"
                            className={styles.pdfBtn}
                            onClick={() => downloadPdf(inv._id, inv.invoiceNumber)}
                          >
                            PDF
                          </button>
                          <button
                            type="button"
                            className={styles.pdfBtn}
                            onClick={() => printPdf(inv._id)}
                          >
                            Imprimer
                          </button>
                          <button
                            className={`${styles.emailBtn} ${inv.emailStatus === 'sent' ? styles.resendBtn : ''}`}
                            disabled={sending}
                            onClick={() => requestSendOne(inv._id, inv.emailStatus === 'sent', inv.coach?.name)}
                          >
                            {inv.emailStatus === 'sent' ? 'Renvoyer' : 'Envoyer'}
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className={styles.pagination}>
                <button
                  className={styles.pageBtn}
                  disabled={safePage <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  ← Précédent
                </button>
                <span className={styles.pageLabel}>
                  Page {safePage} sur {totalPages}
                </span>
                <button
                  className={styles.pageBtn}
                  disabled={safePage >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Suivant →
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {admin && campaign.status === 'completed' && (
        <div className={`${styles.panel} ${styles.virementPanel}`}>
          <div className={styles.virementHeader}>
            <div>
              <h2>Virement mensuel MyBIAT</h2>
              <p className={styles.virementHint}>
                Téléchargez le fichier Excel pré-rempli pour l&apos;importer sur la plateforme MyBIAT
                et effectuer les virements aux coaches.
              </p>
            </div>
            <button
              type="button"
              className={styles.virementBtn}
              disabled={
                downloadingVirement
                || virementLoading
                || !virementPreview?.beneficiaryCount
                || !virementPreview?.ribDonneurConfigured
              }
              onClick={handleDownloadVirement}
            >
              {downloadingVirement ? 'Génération…' : 'Télécharger le fichier Excel'}
            </button>
          </div>

          {virementLoading ? (
            <div className={styles.virementLoading}>
              <Spinner />
              <span>Préparation du fichier virement…</span>
            </div>
          ) : virementError ? (
            <p className={styles.virementError}>{virementError}</p>
          ) : virementPreview ? (
            <>
              {!virementPreview.ribDonneurConfigured && (
                <p className={styles.virementError}>
                  RIB donneur d&apos;ordre non configuré (VIREMENT_RIB_DONNEUR sur le serveur).
                </p>
              )}

              <div className={styles.virementStats}>
                <div className={styles.virementStat}>
                  <span>Bénéficiaires</span>
                  <strong>{virementPreview.beneficiaryCount}</strong>
                </div>
                <div className={styles.virementStat}>
                  <span>Montant total</span>
                  <strong>{formatTND(virementPreview.totalTnd)} TND</strong>
                </div>
                <div className={styles.virementStat}>
                  <span>Sans RIB</span>
                  <strong>{virementPreview.missingRibCount}</strong>
                </div>
                <div className={styles.virementStat}>
                  <span>Échéance</span>
                  <strong>{virementPreview.dueDate}</strong>
                </div>
              </div>

              {virementPreview.missingRibCount > 0 && (
                <div className={styles.virementWarn}>
                  <strong>{virementPreview.missingRibCount}</strong> coach(es) sans RIB — exclus du fichier.
                  Complétez la colonne H de l&apos;import Excel ou modifiez la fiche coach.
                  <ul className={styles.virementMissingList}>
                    {virementPreview.missingRib.slice(0, 6).map((row) => (
                      <li key={row.invoiceId}>
                        {row.coachName} ({row.invoiceNumber})
                      </li>
                    ))}
                    {virementPreview.missingRibCount > 6 && (
                      <li>… et {virementPreview.missingRibCount - 6} autre(s)</li>
                    )}
                  </ul>
                </div>
              )}
            </>
          ) : null}
        </div>
      )}

      <ConfirmModal
        open={!!confirmModal}
        variant={confirmModal?.variant || 'info'}
        title={confirmModal?.title}
        message={confirmModal?.message}
        detail={confirmModal?.detail}
        confirmLabel={confirmModal?.confirmLabel || 'Confirmer'}
        cancelLabel={confirmModal?.cancelLabel || 'Annuler'}
        onConfirm={handleConfirmModal}
        onCancel={() => setConfirmModal(null)}
        loading={sending || deleting}
      />
    </div>
  )
}

export default CampagneDetail
